/**
 * NotificationSystem component using MUI Snackbar for success/failure notifications
 * Requirements: 1.4, 5.5
 */

import React from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { useUI } from '../../contexts/UIContext';
import { Notification } from '../../types';

/**
 * Main notification system component that displays all active notifications
 */
export function NotificationSystem() {
  const { state, actions } = useUI();

  const handleClose = (notificationId: string) => {
    actions.removeNotification(notificationId);
  };

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        maxWidth: 400,
      }}
    >
      {state.notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => handleClose(notification.id)}
        />
      ))}
    </Stack>
  );
}

/**
 * Individual notification item component
 */
interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

function NotificationItem({ notification, onClose }: NotificationItemProps) {
  const [open, setOpen] = React.useState(true);

  const handleClose = () => {
    setOpen(false);
    // Delay the actual removal to allow exit animation
    setTimeout(onClose, 150);
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return undefined;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={notification.autoHide !== false ? (notification.duration || 5000) : null}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity={notification.type}
        icon={getIcon()}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ width: '100%' }}
      >
        {notification.message}
      </Alert>
    </Snackbar>
  );
}

/**
 * Specialized notification components for common use cases
 */

export function DocumentLoadedNotification({ documentName }: { documentName: string }) {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'success',
      message: `Document "${documentName}" loaded successfully`,
      autoHide: true,
      duration: 3000,
    });
  }, [documentName, actions]);

  return null;
}

export function ValidationSubmittedNotification({ submissionId }: { submissionId: string }) {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'success',
      message: `Validation submitted successfully (ID: ${submissionId})`,
      autoHide: true,
      duration: 4000,
    });
  }, [submissionId, actions]);

  return null;
}

export function AutoSaveNotification() {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'info',
      message: 'Changes saved automatically',
      autoHide: true,
      duration: 2000,
    });
  }, [actions]);

  return null;
}

export function ValidationErrorNotification({ errors }: { errors: string[] }) {
  const { actions } = useUI();

  React.useEffect(() => {
    const errorMessage = errors.length === 1 
      ? `Validation error: ${errors[0]}`
      : `${errors.length} validation errors found. Please check your input.`;

    actions.addNotification({
      type: 'error',
      message: errorMessage,
      autoHide: true,
      duration: 6000,
    });
  }, [errors, actions]);

  return null;
}

export function NetworkErrorNotification({ retry }: { retry?: () => void }) {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'error',
      message: retry 
        ? 'Network connection error. Please check your internet connection and try again.'
        : 'Network connection error. Please check your internet connection.',
      autoHide: false, // Keep visible until user dismisses
    });
  }, [actions, retry]);

  return null;
}

export function PDFLoadErrorNotification({ error }: { error: string }) {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'error',
      message: `Failed to load PDF: ${error}`,
      autoHide: true,
      duration: 8000,
    });
  }, [error, actions]);

  return null;
}

export function CoordinateErrorNotification({ fieldName }: { fieldName: string }) {
  const { actions } = useUI();

  React.useEffect(() => {
    actions.addNotification({
      type: 'warning',
      message: `Highlighting disabled for "${fieldName}" due to coordinate errors`,
      autoHide: true,
      duration: 5000,
    });
  }, [fieldName, actions]);

  return null;
}

/**
 * Hook for showing notifications with consistent patterns
 */
export function useNotificationHelpers() {
  const { actions } = useUI();

  const showSuccess = React.useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'success',
      message,
      autoHide: true,
      duration: duration || 4000,
    });
  }, [actions]);

  const showError = React.useCallback((message: string, persistent?: boolean) => {
    actions.addNotification({
      type: 'error',
      message,
      autoHide: !persistent,
      duration: persistent ? undefined : 6000,
    });
  }, [actions]);

  const showWarning = React.useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'warning',
      message,
      autoHide: true,
      duration: duration || 5000,
    });
  }, [actions]);

  const showInfo = React.useCallback((message: string, duration?: number) => {
    actions.addNotification({
      type: 'info',
      message,
      autoHide: true,
      duration: duration || 4000,
    });
  }, [actions]);

  const showDocumentLoaded = React.useCallback((documentName: string) => {
    showSuccess(`Document "${documentName}" loaded successfully`, 3000);
  }, [showSuccess]);

  const showValidationSubmitted = React.useCallback((submissionId: string) => {
    showSuccess(`Validation submitted successfully (ID: ${submissionId})`, 4000);
  }, [showSuccess]);

  const showAutoSave = React.useCallback(() => {
    showInfo('Changes saved automatically', 2000);
  }, [showInfo]);

  const showValidationErrors = React.useCallback((errors: string[]) => {
    const message = errors.length === 1 
      ? `Validation error: ${errors[0]}`
      : `${errors.length} validation errors found. Please check your input.`;
    showError(message);
  }, [showError]);

  const showNetworkError = React.useCallback(() => {
    showError('Network connection error. Please check your internet connection.', true);
  }, [showError]);

  const showPDFLoadError = React.useCallback((error: string) => {
    showError(`Failed to load PDF: ${error}`);
  }, [showError]);

  const showCoordinateError = React.useCallback((fieldName: string) => {
    showWarning(`Highlighting disabled for "${fieldName}" due to coordinate errors`);
  }, [showWarning]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showDocumentLoaded,
    showValidationSubmitted,
    showAutoSave,
    showValidationErrors,
    showNetworkError,
    showPDFLoadError,
    showCoordinateError,
  };
}

export default NotificationSystem;